import { useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ehRotaPublica } from "@/router/public-routes";
import { getMe } from "@/services/api/auth";
import { onUnauthorized } from "@/services/api/client";
import { queryKeys } from "@/services/api/query-keys";

import { AuthContext, type AuthContextValue } from "./useAuth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // O app do participante (`/app`) mora nesta mesma SPA e é público. Sem este
  // gate, o `getMe` abaixo dispara na montagem, volta 401, e o handler de 401
  // manda o visitante para o login — a rota respondia 200 no servidor e o chat
  // ficava inalcançável no navegador. Ver `router/public-routes.ts`.
  const publica = ehRotaPublica(useLocation().pathname);

  const meQuery = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: getMe,
    // 401 aqui significa "sem sessão" (fluxo normal de quem não logou
    // ainda), não uma falha transitória de rede — não vale a pena repetir.
    retry: false,
    staleTime: 60_000,
    enabled: !publica,
  });

  useEffect(() => {
    // Registro único do callback de 401 do cliente de API (ver
    // `services/api/client.ts`): ele nunca redireciona sozinho, só avisa.
    // Quem decide o que fazer é o Shell — aqui limpamos o cache de todo
    // domínio que não seja "auth" (nada do usuário anterior deve sobreviver
    // numa sessão nova) e navegamos para /login pelo router (nunca
    // `window.location`, para não perder o estado da SPA e para respeitar o
    // `basename="/ui"`).
    //
    // IMPORTANTE: de propósito NÃO tocamos na query `auth.me` aqui (nem
    // `removeQueries`, nem `invalidateQueries`). Este callback é disparado
    // DE DENTRO da própria resposta 401 — se ele também derrubasse/
    // invalidasse `auth.me`, o observer ainda montado (`AuthProvider` nunca
    // desmonta) buscaria de novo, poderia levar outro 401 e reentrar aqui:
    // um loop. `auth.me` já reflete sozinho o estado "sem sessão" (a query
    // que 401'ou termina em `status: "error"`, `data: undefined` — é
    // exatamente o que `isAuthenticated` abaixo já lê). Só volta a mudar
    // quando o próximo login bem-sucedido invalidar essa key explicitamente
    // (ver `pages/Login`).
    onUnauthorized(() => {
      // Numa rota pública o 401 é esperado e não significa sessão expirada:
      // o participante nunca teve sessão. Mandá-lo para o login tiraria do ar
      // a tela que ele tem direito de usar.
      if (publica) return;
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
      navigate("/login", { replace: true });
    });
  }, [navigate, queryClient, publica]);

  const me = meQuery.data ?? null;
  const isAuthenticated = Boolean(me);
  const isOrg = !me || !(me.role === "operacional" && !me.is_super);

  const value: AuthContextValue = {
    me,
    isLoading: meQuery.isPending,
    isAuthenticated,
    isOrg,
    refetchMe: () => void meQuery.refetch(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
